import React from 'react';
import {
  Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, XAxis,
} from 'recharts';
import classNames from 'classnames';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { FACET_COLORS, PALETTE } from 'Theme';
import PropTypes from 'prop-types';
import CollapsibleDisclaimer
  from 'Components/ComponentLibrary/Components/Uncategorized/CollapsibleDisclaimer';

const useStyles = buildUseStyles('AccountProposalReviewTemplate', (theme => ({
  firstSection: {
    marginTop: theme.spacing(2),
  },
  section: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  chartContainer: {
    borderBottom: `1px dotted ${theme.palette.facet.mediumGray}`,
  },
  disclaimer: {
    marginTop: theme.spacing(2),
  },
})));

const Content = ({
  oneYearMaxLoss,
  threeYearMaxAnnualizedLosses,
  averageAnnualReturn,
  threeYearMaxAnnualizedReturn,
  oneYearMaxReturn,
  disclaimer,
  disclaimerReadMore,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const data = [
    {
      name: oneYearMaxLoss,
      uv: parseFloat(oneYearMaxLoss),
    },
    {
      name: threeYearMaxAnnualizedLosses,
      uv: parseFloat(threeYearMaxAnnualizedLosses),
    },
    {
      name: averageAnnualReturn,
      uv: parseFloat(averageAnnualReturn),
    },
    {
      name: threeYearMaxAnnualizedReturn,
      uv: parseFloat(threeYearMaxAnnualizedReturn),
    },
    {
      name: oneYearMaxReturn,
      uv: parseFloat(oneYearMaxReturn),
    },
  ];

  const colors = [FACET_COLORS.red.color, FACET_COLORS.orange.color, PALETTE.success.main, FACET_COLORS.accentBlue.color];

  return (
    <div>
      <ResponsiveContainer height={200} className={classes.chartContainer}>
        <BarChart
          data={data}
          height={200}
          margin={{
            top: 0, right: 0, bottom: 0, left: 0,
          }}
        >
          <ReferenceLine strokeDasharray="2 2" y={0} stroke={FACET_COLORS.primaryGray.color} />
          <Bar dataKey="uv" fill="#000">
            {data.map((entry, index) => (
              <Cell key={`cell-${entry.name}`} fill={colors[index] || FACET_COLORS.primaryBlue.color} />
            ))}
          </Bar>
          <XAxis dataKey="name" tickSize={0} axisLine={false} />
        </BarChart>
      </ResponsiveContainer>

      <div className={classNames(classes.section, classes.firstSection)}>
        <Text variant="body" weight="bold" color="error" align="start" text="1 Year Maximum Loss" />
        <Text variant="body" weight="bold" color="error" align="start" text={oneYearMaxLoss} />
      </div>
      <div className={classes.section}>
        <Text variant="body" weight="bold" color="warning" align="start" text="3 Year Maximum Annualized Loss" />
        <Text variant="body" weight="bold" color="warning" align="start" text={threeYearMaxAnnualizedLosses} />
      </div>
      <div className={classes.section}>
        <Text variant="body" weight="bold" color="success" align="start" text="Average Annual Return" />
        <Text variant="body" weight="bold" color="success" align="start" text={averageAnnualReturn} />
      </div>
      <div className={classes.section}>
        <Text variant="body" weight="bold" color="accentBlue" align="start" text="3 Year Maximum Annualized Return" />
        <Text variant="body" weight="bold" color="accentBlue" align="start" text={threeYearMaxAnnualizedReturn} />
      </div>
      <div className={classes.section}>
        <Text variant="body" weight="bold" align="start" text="1 Year Maximum Return" />
        <Text variant="body" weight="bold" align="start" text={oneYearMaxReturn} />
      </div>
      <CollapsibleDisclaimer
        disclaimerTitle={'Hypothetical Investment Disclaimer'}
        className={classes.disclaimer}
        disclaimerText={disclaimer}
        disclaimerReadMore={disclaimerReadMore}
      />
    </div>
  );
};

Content.defaultProps = {
  disclaimerReadMore: undefined,
};

Content.propTypes = {
  oneYearMaxLoss: PropTypes.string.isRequired,
  threeYearMaxAnnualizedLosses: PropTypes.string.isRequired,
  averageAnnualReturn: PropTypes.string.isRequired,
  threeYearMaxAnnualizedReturn: PropTypes.string.isRequired,
  oneYearMaxReturn: PropTypes.string.isRequired,
  disclaimer: PropTypes.string.isRequired,
  disclaimerReadMore: PropTypes.string,
};

export default Content;
